import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { formatDateTimeDisplay } from '../../utils/dateUtils';
import {
  Box,
  Heading,
  Text,
  Badge,
  Flex,
  Button,
  IconButton,
  VStack,
  HStack,
  Divider,
  useToast,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Spinner,
  Center,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Select,
  Link,
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionButton,
  AccordionIcon,
  Card,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  List,
  ListItem,
  CloseButton,
} from '@chakra-ui/react';
import {
  ChevronRightIcon,
  EditIcon,
  ChevronDownIcon,
  AttachmentIcon,
  DeleteIcon,
  LinkIcon,
} from '@chakra-ui/icons';
import { useAuth } from '../../contexts/AuthContext';
import ticketService from '../../services/ticketService';
import usersService from '../../services/usersService';
import CommentSection from '../../components/CommentSection';
import DueDateDisplay from '../../components/DueDateDisplay';

const TicketDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [availableTickets, setAvailableTickets] = useState([]);
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [ticketSearch, setTicketSearch] = useState('');
  const [isLoadingTickets, setIsLoadingTickets] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const {
    isOpen: isConnectOpen,
    onOpen: onConnectOpen,
    onClose: onConnectClose
  } = useDisclosure();
  const {
    isOpen: isAssignOpen,
    onOpen: onAssignOpen,
    onClose: onAssignClose
  } = useDisclosure();

  const [users, setUsers] = useState([]);
  const [assignToUser, setAssignToUser] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);

  const [ticket, setTicket] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [auditLogs, setAuditLogs] = useState([]);
  const [isAuditLoading, setIsAuditLoading] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const fetchTicket = async () => {
      try {
        setIsLoading(true);
        const response = await ticketService.getTicket(id);
        setTicket(response.data.data);
        setNewStatus(response.data.data.status);

        // Set the initial assignedTo value if there's an assignee
        if (response.data.data.assignedTo) {
          setAssignToUser(response.data.data.assignedTo._id);
        }
      } catch (error) {
        toast({
          title: 'Error fetching ticket',
          description: error.response?.data?.message || 'Something went wrong',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        navigate('/tickets');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTicket();
  }, [id, navigate, toast]);

  const fetchAuditLogs = async () => {
    try {
      setIsAuditLoading(true);
      const response = await ticketService.getAuditLogs(id);
      setAuditLogs(response.data.data);
    } catch (error) {
      toast({
        title: 'Error fetching audit logs',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsAuditLoading(false);
    }
  };

  const handleStatusChange = async () => {
    if (newStatus === ticket.status) {
      onClose();
      return;
    }

    try {
      setIsUpdating(true);
      await ticketService.updateStatus(id, newStatus);

      setTicket(prev => ({
        ...prev,
        status: newStatus
      }));

      toast({
        title: 'Status updated',
        description: `Ticket status changed to ${newStatus}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onClose();
    } catch (error) {
      toast({
        title: 'Error updating status',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await ticketService.deleteTicket(id);

      toast({
        title: 'Ticket deleted',
        description: 'Ticket has been successfully deleted',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      navigate('/tickets');
    } catch (error) {
      toast({
        title: 'Error deleting ticket',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setIsDeleting(false);
    }
  };

  // Helper function to get the appropriate badge color for ticket status
  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'blue';
      case 'in-progress':
        return 'yellow';
      case 'resolved':
        return 'green';
      case 'closed':
        return 'gray';
      default:
        return 'gray';
    }
  };

  // Helper function to get the appropriate badge color for ticket priority
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'red';
      case 'medium':
        return 'yellow';
      case 'low':
        return 'green';
      default:
        return 'gray';
    }
  };

  // Load available tickets for connecting
  const loadAvailableTickets = async () => {
    try {
      setIsLoadingTickets(true);
      const response = await ticketService.getTickets({
        search: ticketSearch,
        limit: 10
      });
      // Filter out current ticket and already connected tickets
      const filtered = response.data.data.filter(t =>
        t._id !== id &&
        !ticket.connectedTickets?.some(ct => ct._id === t._id)
      );
      setAvailableTickets(filtered);
    } catch (error) {
      toast({
        title: 'Error loading tickets',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoadingTickets(false);
    }
  };

  // Handle connecting tickets
  const handleConnectTickets = async () => {
    if (!selectedTickets.length) return;

    try {
      setIsConnecting(true);
      await ticketService.connectTickets(id, selectedTickets);

      // Refresh ticket data to get updated connections
      const response = await ticketService.getTicket(id);
      setTicket(response.data.data);

      toast({
        title: 'Tickets connected',
        description: 'The tickets have been connected successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onConnectClose();
      setSelectedTickets([]);
    } catch (error) {
      toast({
        title: 'Error connecting tickets',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsConnecting(false);
    }
  };

  // Handle disconnecting a ticket
  const handleDisconnectTicket = async (connectedId) => {
    try {
      await ticketService.disconnectTickets(id, connectedId);

      // Refresh ticket data to get updated connections
      const response = await ticketService.getTicket(id);
      setTicket(response.data.data);

      toast({
        title: 'Ticket disconnected',
        description: 'The ticket has been disconnected successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error disconnecting ticket',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Load users for assignment
  const loadUsers = async () => {
    try {
      const usersResponse = await usersService.getUsers({
        'role': 'admin,support'
      });
      setUsers(usersResponse.data.data);
    } catch (error) {
      toast({
        title: 'Error loading users',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Handle ticket assignment
  const handleAssignTicket = async () => {
    try {
      setIsAssigning(true);
      await ticketService.assignTicket(id, assignToUser);

      // Update the local state
      const response = await ticketService.getTicket(id);
      setTicket(response.data.data);

      toast({
        title: 'Ticket assigned',
        description: assignToUser ? `Ticket has been assigned successfully` : `Ticket has been unassigned successfully`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onAssignClose();
    } catch (error) {
      toast({
        title: 'Error assigning ticket',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsAssigning(false);
    }
  };

  if (isLoading) {
    return (
      <Center h="200px">
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="gray.200"
          color="brand.500"
          size="xl"
        />
      </Center>
    );
  }

  if (!ticket) {
    return <Box>Ticket not found</Box>;
  }

  return (
    <Box>
      <Breadcrumb
        spacing="8px"
        separator={<ChevronRightIcon color="gray.500" />}
        mb={6}
      >
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/dashboard">
            Dashboard
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/tickets">
            Tickets
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Ticket Details</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">{ticket.title}</Heading>
        <HStack>
          {user?.role !== 'support' && (
            <Button
              as={RouterLink}
              to={`/tickets/${id}/edit`}
              leftIcon={<EditIcon />}
              colorScheme="brand"
              variant="outline"
            >
              Edit
            </Button>
          )}
          <Menu>
            <MenuButton
              as={Button}
              rightIcon={<ChevronDownIcon />}
              colorScheme="brand"
            >
              Actions
            </MenuButton>
            <MenuList>
              <MenuItem onClick={onOpen}>Change Status</MenuItem>
              {(user?.role === 'admin' || user?.role === 'support') && (
                <MenuItem
                  onClick={() => {
                    loadUsers();
                    onAssignOpen();
                  }}
                >
                  Assign Ticket
                </MenuItem>
              )}
              {user?.role !== 'support' && (
                <MenuItem
                  color="red.500"
                  icon={<DeleteIcon />}
                  onClick={handleDelete}
                  isDisabled={isDeleting}
                >
                  Delete Ticket
                </MenuItem>
              )}
            </MenuList>
          </Menu>
        </HStack>
      </Flex>

      <VStack align="stretch" spacing={6}>
        {/* Ticket Details Card */}
        <Box bg="white" borderRadius="md" p={6} boxShadow="sm">
          <HStack spacing={4} mb={4}>
            <Badge colorScheme={getStatusColor(ticket.status)} fontSize="sm" px={2} py={1}>
              {ticket.status}
            </Badge>
            <Badge colorScheme={getPriorityColor(ticket.priority)} fontSize="sm" px={2} py={1}>
              {ticket.priority} priority
            </Badge>
          </HStack>

          <Flex gap={8}>
            {/* Left Column - Main Information */}
            <VStack align="stretch" spacing={4} flex="1">
              <Box>
                <Text fontWeight="bold" color="gray.500" fontSize="sm">
                  PROJECT
                </Text>
                <Text fontSize="md">{ticket.project.name}</Text>
              </Box>

              <Box>
                <Text fontWeight="bold" color="gray.500" fontSize="sm">
                  DESCRIPTION
                </Text>
                <Text whiteSpace="pre-wrap">{ticket.description}</Text>
              </Box>

              {ticket.assignedTo && (
                <Box>
                  <Text fontWeight="bold" color="gray.500" fontSize="sm">
                    ASSIGNED TO
                  </Text>
                  <Text>{ticket.assignedTo.name}</Text>
                </Box>
              )}
              <Box>
                <Text fontWeight="bold" color="gray.500" fontSize="sm">
                  SUBMITTED BY
                </Text>
                <Text>{ticket.submittedBy.name}</Text>
              </Box>
            </VStack>

            {/* Right Column - Date Information */}
            <VStack align="stretch" spacing={4} minW="200px">
              <Box>
                <Text fontWeight="bold" color="gray.500" fontSize="sm">
                  CREATED ON
                </Text>
                <Text>
                  {formatDateTimeDisplay(ticket.createdAt)}
                </Text>
              </Box>
              {ticket.dueDate && (
                <Box>
                  <Text fontWeight="bold" color="gray.500" fontSize="sm">
                    DUE DATE
                  </Text>
                  <DueDateDisplay dueDate={ticket.dueDate} ticketStatus={ticket.status} />
                </Box>
              )}
              {ticket.resolvedDate && (
                <Box>
                  <Text fontWeight="bold" color="gray.500" fontSize="sm">
                    RESOLVED ON
                  </Text>
                  <Text>
                    {formatDateTimeDisplay(ticket.resolvedDate)}
                  </Text>
                </Box>
              )}
            </VStack>
          </Flex>
        </Box>

        {/* Attachments Section */}
        {ticket.attachments && ticket.attachments.length > 0 && (
          <Box bg="white" borderRadius="md" p={6} boxShadow="sm">
            <Heading size="md" mb={4}>
              Attachments
            </Heading>
            <Divider mb={4} />
            <VStack align="stretch" spacing={2}>
              {ticket.attachments.map((file, index) => (
                <HStack key={index} p={2} bg="gray.50" borderRadius="md">
                  <AttachmentIcon color="gray.500" />
                  <Link color="brand.500" href={`${process.env.REACT_APP_API_URL}/tickets/${ticket._id}/attachments/${file._id}`} isExternal>
                    {file.originalname}
                  </Link>
                  <Text color="gray.500" fontSize="sm" ml="auto">
                    {(file.size / 1024).toFixed(2)} KB
                  </Text>
                </HStack>
              ))}
            </VStack>
          </Box>
        )}

        {/* Comments Section */}
        <Box bg="white" borderRadius="md" p={6} boxShadow="sm">
          <CommentSection ticketId={ticket._id} />
        </Box>

        {/* Connected Tickets Section */}
        <Box bg="white" borderRadius="md" p={6} boxShadow="sm">
          <Flex justify="space-between" align="center" mb={4}>
            <Heading size="md">Connected Tickets</Heading>
            <Button
              leftIcon={<LinkIcon />}
              colorScheme="brand"
              variant="outline"
              size="sm"
              onClick={() => {
                onConnectOpen();
                loadAvailableTickets();
              }}
            >
              Connect Tickets
            </Button>
          </Flex>

          {ticket.connectedTickets && ticket.connectedTickets.length > 0 ? (
            <VStack align="stretch" spacing={3}>
              {ticket.connectedTickets.map((connectedTicket) => (
                <Flex
                  key={connectedTicket._id}
                  p={3}
                  bg="gray.50"
                  borderRadius="md"
                  justify="space-between"
                  align="center"
                >
                  <HStack spacing={4}>
                    <Badge colorScheme={getStatusColor(connectedTicket.status)}>
                      {connectedTicket.status}
                    </Badge>
                    <Link
                      as={RouterLink}
                      to={`/tickets/${connectedTicket._id}`}
                      color="brand.600"
                      fontWeight="medium"
                    >
                      {connectedTicket.title}
                    </Link>
                  </HStack>
                  <IconButton
                    icon={<LinkIcon />}
                    variant="ghost"
                    colorScheme="red"
                    size="sm"
                    aria-label="Disconnect ticket"
                    onClick={() => handleDisconnectTicket(connectedTicket._id)}
                  />
                </Flex>
              ))}
            </VStack>
          ) : (
            <Text color="gray.500">No connected tickets</Text>
          )}
        </Box>
      </VStack>

      {/* Status Change Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Change Ticket Status</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4}>
              Update the status of ticket: <strong>{ticket.title}</strong>
            </Text>
            <Select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
            >
              <option value="open">Open</option>
              <option value="in-progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </Select>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="brand"
              isLoading={isUpdating}
              onClick={handleStatusChange}
            >
              Update Status
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {user?.role === 'admin' && (
        <Accordion allowToggle mt={6}>
          <AccordionItem>
            <h2>
              <AccordionButton
                onClick={() => !auditLogs.length && fetchAuditLogs()}
                bg="brand.500"
                color="white"
                _hover={{ bg: "brand.600" }}
                _expanded={{ bg: "brand.700", color: "brand.100" }}
                borderRadius="md"
                p={4}
                boxShadow="lg"
                transition="background-color 0.3s ease"
              >
                <Box flex="1" textAlign="left">
                  Audit Logs
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
              {isAuditLoading ? (
                <Center>
                  <Spinner size="md" />
                </Center>
              ) : auditLogs.length > 0 ? (
                <VStack align="stretch" spacing={4}>
                  {auditLogs.map((log, index) => (
                    <Card key={index} p={4} bg="gray.50" borderRadius="md" boxShadow="sm">
                      <HStack justify="space-between" w="100%">
                        <HStack spacing={4}>
                          <Badge colorScheme={
                            log.action === 'CREATE' ? 'green' :
                              log.action === 'UPDATE' ? 'yellow' :
                                'red'
                          }>
                            {log.action}
                          </Badge>
                          <Text>{log.userId?.name || 'Unknown'} ({log.userId?.email || 'Unknown email'})</Text>
                        </HStack>
                        <Text fontSize="sm" color="gray.600">
                          {formatDateTimeDisplay(log.timestamp)}
                        </Text>
                      </HStack>
                      {log.changes && Object.keys(log.changes).length > 0 && (
                        <Box mt={4} p={3} bg="gray.100" borderRadius="md">
                          <Text fontWeight="bold" mb={2}>Changes:</Text>
                          <Table variant="simple" size="sm">
                            <Thead>
                              <Tr>
                                <Th>Field</Th>
                                <Th>Before</Th>
                                <Th>After</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {Object.entries(log.changes).map(([field, values]) => (
                                <Tr key={field}>
                                  <Td>{field}</Td>
                                  <Td>{values.before !== undefined ? JSON.stringify(values.before?.name || values.before) : '-'}</Td>
                                  <Td>{values.after !== undefined ? JSON.stringify(values.after?.name || values.after) : '-'}</Td>
                                </Tr>
                              ))}
                            </Tbody>
                          </Table>
                        </Box>
                      )}
                    </Card>
                  ))}
                </VStack>
              ) : (
                <Text>No audit logs found.</Text>
              )}
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      )}

      {/* Connect Tickets Modal */}
      <Modal isOpen={isConnectOpen} onClose={onConnectClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Connect Tickets</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Input
                placeholder="Search tickets..."
                value={ticketSearch}
                onChange={(e) => setTicketSearch(e.target.value)}
                onKeyUp={(e) => {
                  if (e.key === 'Enter') {
                    loadAvailableTickets();
                  }
                }}
              />

              {selectedTickets.length > 0 && (
                <Box>
                  <Text fontWeight="medium" mb={2}>Selected Tickets:</Text>
                  <List spacing={2}>
                    {selectedTickets.map(ticketId => {
                      const ticket = availableTickets.find(t => t._id === ticketId);
                      return ticket ? (
                        <ListItem
                          key={ticket._id}
                          p={2}
                          bg="gray.50"
                          borderRadius="md"
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Text>{ticket.title}</Text>
                          <CloseButton
                            size="sm"
                            onClick={() => setSelectedTickets(prev =>
                              prev.filter(id => id !== ticket._id)
                            )}
                          />
                        </ListItem>
                      ) : null;
                    })}
                  </List>
                </Box>
              )}

              <Box>
                <Text fontWeight="medium" mb={2}>Available Tickets:</Text>
                {isLoadingTickets ? (
                  <Center p={4}>
                    <Spinner size="sm" />
                  </Center>
                ) : (
                  <List spacing={2}>
                    {availableTickets.map(ticket => (
                      <ListItem
                        key={ticket._id}
                        p={2}
                        bg="gray.50"
                        borderRadius="md"
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        cursor="pointer"
                        onClick={() => {
                          if (!selectedTickets.includes(ticket._id)) {
                            setSelectedTickets(prev => [...prev, ticket._id]);
                          }
                        }}
                        _hover={{ bg: 'gray.100' }}
                      >
                        <HStack>
                          <Badge colorScheme={getStatusColor(ticket.status)}>
                            {ticket.status}
                          </Badge>
                          <Text>{ticket.title}</Text>
                        </HStack>
                        <Badge>{ticket.project.name}</Badge>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onConnectClose}>
              Cancel
            </Button>
            <Button
              colorScheme="brand"
              isLoading={isConnecting}
              onClick={handleConnectTickets}
              isDisabled={selectedTickets.length === 0}
            >
              Connect Selected Tickets
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Assign Ticket Modal */}
      <Modal isOpen={isAssignOpen} onClose={onAssignClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Assign Ticket</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4}>
              Assign ticket "<strong>{ticket.title}</strong>" to:
            </Text>
            <Select
              value={assignToUser}
              onChange={(e) => setAssignToUser(e.target.value)}
              placeholder="Select staff member"
            >
              <option value="">Unassigned</option>
              {users.map(user => (
                <option key={user._id} value={user._id}>
                  {user.name}
                </option>
              ))}
            </Select>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onAssignClose}>
              Cancel
            </Button>
            <Button
              colorScheme="brand"
              isLoading={isAssigning}
              onClick={handleAssignTicket}
            >
              Assign Ticket
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default TicketDetails;
